import { apiClient } from './client'
import type { ApiResponse } from './client'

export type CenterInquiryCategory = 'content' | 'system' | 'billing' | 'policy' | 'other'
export type CenterInquiryStatus = 'pending' | 'answered'

export interface AttachmentResponse {
  attachmentId: number
  fileName: string
  fileSize: number
  downloadUrl: string | null
}

export interface ReplyResponse {
  replyId: number
  authorName: string
  authorRole: string
  content: string
  createdAt: string
  attachments: AttachmentResponse[]
}

export interface CenterInquiryListItem {
  inquiryId: number
  source: 'lc_center'
  organizationName: string | null
  userName: string | null
  userEmail: string | null
  category: CenterInquiryCategory
  title: string
  status: CenterInquiryStatus
  replyCount: number
  createdAt: string
  answeredAt: string | null
  hasAttachments: boolean
}

export interface CenterInquiryDetail extends CenterInquiryListItem {
  content: string
  attachments: AttachmentResponse[]
  replies: ReplyResponse[]
}

export interface CenterInquiryListResponse {
  content: CenterInquiryListItem[]
  totalElements: number
  totalPages: number
  page: number
  size: number
}

export const adminSupportInquiriesApi = {
  list: async (params: {
    status?: CenterInquiryStatus
    category?: CenterInquiryCategory
    keyword?: string
    page?: number
    size?: number
  }): Promise<CenterInquiryListResponse> => {
    const response = await apiClient.get<ApiResponse<CenterInquiryListResponse>>(
      '/lc/admin/support-inquiries',
      { params },
    )

    if (!response.data.data) {
      throw new Error('Failed to fetch support inquiries')
    }

    return response.data.data
  },

  detail: async (inquiryId: number): Promise<CenterInquiryDetail> => {
    const response = await apiClient.get<ApiResponse<CenterInquiryDetail>>(
      `/lc/admin/support-inquiries/${inquiryId}`,
    )

    if (!response.data.data) {
      throw new Error('Support inquiry not found')
    }

    return response.data.data
  },

  create: async (payload: {
    category: CenterInquiryCategory
    title: string
    content: string
    files: File[]
  }): Promise<{ inquiryId: number }> => {
    const formData = new FormData()
    formData.append('category', payload.category)
    formData.append('title', payload.title)
    formData.append('content', payload.content)
    payload.files.forEach((file) => formData.append('files', file))

    const response = await apiClient.post<ApiResponse<{ inquiryId: number }>>(
      '/lc/admin/support-inquiries',
      formData,
      { headers: { 'Content-Type': undefined } },
    )

    if (!response.data.data) {
      throw new Error('Failed to create support inquiry')
    }

    return response.data.data
  },
}
